import type { Tax } from '@monorepo/shared/schemas/taxes'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTaxesList } from '@/features/taxes/hooks/useTaxes'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

interface TaxDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingTaxes: Array<{ id: string; [key: string]: unknown }>
  onAddTaxRate: (taxRate: Tax, currency: string) => void
  title?: string
  description?: string
  submitButtonText?: string
  allowMultiSelect?: boolean
}

export function TaxDialog({
  open,
  onOpenChange,
  existingTaxes,
  onAddTaxRate,
  title = 'Link Tax Rate',
  description = 'Select a tax rate and currency to apply',
  submitButtonText = 'Link Tax Rate',
  allowMultiSelect = false,
}: TaxDialogProps) {
  const [selectedTaxRate, setSelectedTaxRate] = useState<string>('')
  const [selectedTaxRates, setSelectedTaxRates] = useState<Set<string>>(
    new Set()
  )
  const [taxCurrency, setTaxCurrency] = useState<string>('USD')

  // Fetch active taxes from catalog using React Query hook
  const { taxes, isLoading, isError } = useTaxesList({
    is_active: true,
    limit: 100,
  })

  // Filter out already assigned taxes
  const availableOptions = taxes.filter(
    tax => !existingTaxes.some(existing => existing.id === tax.id)
  )

  const handleAddTaxRate = () => {
    const taxRate = taxes.find(t => t.id === selectedTaxRate)
    if (taxRate && !existingTaxes.some(t => t.id === taxRate.id)) {
      onAddTaxRate(taxRate, taxCurrency)
      setSelectedTaxRate('')
      setTaxCurrency('USD')
      onOpenChange(false)
    }
  }

  const handleToggleTaxRate = (taxId: string) => {
    const newSelection = new Set(selectedTaxRates)
    if (newSelection.has(taxId)) {
      newSelection.delete(taxId)
    } else {
      newSelection.add(taxId)
    }
    setSelectedTaxRates(newSelection)
  }

  const handleAddMultipleTaxRates = () => {
    const taxRatesToAdd = taxes.filter(t => selectedTaxRates.has(t.id))
    taxRatesToAdd.forEach(taxRate => {
      if (!existingTaxes.some(t => t.id === taxRate.id)) {
        onAddTaxRate(taxRate, taxCurrency)
      }
    })
    setSelectedTaxRates(new Set())
    setTaxCurrency('USD')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg max-h-[80vh]'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className='text-sm text-muted-foreground'>{description}</p>
        </DialogHeader>

        <div className='space-y-4'>
          {isError && (
            <div className='text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200'>
              Failed to load taxes. Please try again.
            </div>
          )}

          {isLoading && (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='w-6 h-6 animate-spin text-muted-foreground' />
              <span className='ml-2 text-sm text-muted-foreground'>
                Loading taxes...
              </span>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {allowMultiSelect ? (
                <div className='space-y-2'>
                  <Label>Select Tax Rates (Multiple)</Label>
                  <div className='border rounded-md max-h-[300px] overflow-y-auto'>
                    {availableOptions.length === 0 ? (
                      <div className='p-4 text-sm text-muted-foreground text-center'>
                        No taxes available. Please create taxes first.
                      </div>
                    ) : (
                      availableOptions.map(tax => {
                        const taxDisplay =
                          tax.tax_type === 'percentage' && tax.percentage_value
                            ? `${tax.percentage_value}%`
                            : tax.tax_type === 'fixed' && tax.fixed_amount_cents
                              ? `$${(tax.fixed_amount_cents / 100).toFixed(2)}`
                              : tax.tax_type

                        return (
                          // biome-ignore lint/a11y/useSemanticElements: styled checkbox row
                          <div
                            key={tax.id}
                            className='flex items-start gap-3 p-3 hover:bg-accent cursor-pointer border-b last:border-b-0'
                            onClick={() => handleToggleTaxRate(tax.id)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ')
                                handleToggleTaxRate(tax.id)
                            }}
                            role='button'
                            tabIndex={0}
                          >
                            <Checkbox
                              checked={selectedTaxRates.has(tax.id)}
                              onCheckedChange={() =>
                                handleToggleTaxRate(tax.id)
                              }
                            />
                            <div className='flex-1'>
                              <div className='font-medium'>{tax.name}</div>
                              <div className='text-sm text-muted-foreground'>
                                {tax.code} - {taxDisplay}
                                {tax.description && ` - ${tax.description}`}
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div className='space-y-2'>
                  <Label>Select Tax Rate</Label>
                  <Select
                    value={selectedTaxRate}
                    onValueChange={setSelectedTaxRate}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Choose a tax rate...'>
                        {selectedTaxRate &&
                          taxes.find(t => t.id === selectedTaxRate)?.name}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableOptions.length === 0 ? (
                        <div className='p-4 text-sm text-muted-foreground text-center'>
                          No taxes available. Please create taxes first.
                        </div>
                      ) : (
                        availableOptions.map(tax => {
                          const taxDisplay =
                            tax.tax_type === 'percentage' &&
                            tax.percentage_value
                              ? `${tax.percentage_value}%`
                              : tax.tax_type === 'fixed' &&
                                  tax.fixed_amount_cents
                                ? `$${(tax.fixed_amount_cents / 100).toFixed(2)}`
                                : tax.tax_type

                          return (
                            <SelectItem key={tax.id} value={tax.id}>
                              <div className='flex flex-col'>
                                <span className='font-medium'>{tax.name}</span>
                                <span className='text-sm text-muted-foreground'>
                                  {tax.code} - {taxDisplay}
                                  {tax.description && ` - ${tax.description}`}
                                </span>
                              </div>
                            </SelectItem>
                          )
                        })
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className='space-y-2'>
                <Label>Currency</Label>
                <Select value={taxCurrency} onValueChange={setTaxCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='USD'>USD</SelectItem>
                    <SelectItem value='EUR'>EUR</SelectItem>
                    <SelectItem value='GBP'>GBP</SelectItem>
                    <SelectItem value='CAD'>CAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='flex justify-end gap-3'>
                <Button variant='outline' onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                {allowMultiSelect ? (
                  <Button
                    onClick={handleAddMultipleTaxRates}
                    disabled={selectedTaxRates.size === 0}
                  >
                    {submitButtonText} ({selectedTaxRates.size})
                  </Button>
                ) : (
                  <Button
                    onClick={handleAddTaxRate}
                    disabled={!selectedTaxRate}
                  >
                    {submitButtonText}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
